import { useState } from 'react';

import { ModalBase } from './ModalBase';

import type React from 'react';

export interface ForceTakeoverModalProps {
  open: boolean;
  attendantName: string;
  /** Mantido por compat de chamada — a prosa do modal não expõe mais o id cru. */
  conversationId?: string;
  isPending: boolean;
  /** true quando o último envio falhou — mostra um `role=alert`. */
  failed: boolean;
  /** Desfecho honesto derivado da RESPOSTA do servidor (null enquanto não houver). */
  outcome: string | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

/** Piso de UI mais estrito que o backend (≥20) — seguro. */
const REASON_FLOOR = 30;

/**
 * ForceTakeoverModal — converte modals-3.jsx (ForceTakeoverModal). Força H_c→H_a
 * com motivo obrigatório (≥30 chars na UI; backend exige ≥20). O desfecho vem da
 * RESPOSTA do servidor (não a cópia "✓ IA encerrou autonomia" do protótipo, que
 * era scaffolding). Guard anti duplo-submit no botão (disabled em `isPending`).
 */
export function ForceTakeoverModal({
  open,
  attendantName,
  isPending,
  failed,
  outcome,
  onClose,
  onConfirm,
}: ForceTakeoverModalProps): React.JSX.Element | null {
  const [reason, setReason] = useState('');
  if (!open) return null;
  const trimmedLen = reason.trim().length;
  const canSubmit = trimmedLen >= REASON_FLOOR && !isPending;

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title="Retomar a conversa da IA para o atendente"
      width={560}
      footer={
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button className="btn-ghost-mini" type="button" onClick={onClose} disabled={isPending}>
            Cancelar
          </button>
          <button
            className="btn-ghost-mini btn-ghost-mini--primary"
            type="button"
            disabled={!canSubmit}
            onClick={() => {
              onConfirm(reason);
            }}
          >
            {isPending ? 'Confirmando…' : 'Confirmar retomada'}
          </button>
        </div>
      }
    >
      <div className="modal-form">
        <div className="modal-form__callout">
          Esta conversa está sendo conduzida autonomamente pela IA. <b>{attendantName}</b> está em
          modo observador.
        </div>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-2)' }}>
          Ao confirmar a retomada forçada:
        </p>
        <ul style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-2)', marginLeft: 20 }}>
          <li>A IA encerra a condução autônoma</li>
          <li>
            A conversa volta para <b>H_a</b> (atendente operando)
          </li>
          <li>O atendente recebe notificação para retomar</li>
        </ul>
        <label className="modal-form__field">
          <span className="modal-form__label">
            Motivo da retomada (obrigatório · mínimo {REASON_FLOOR} caracteres)
          </span>
          <textarea
            className="modal-form__textarea"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            rows={4}
            placeholder="Cliente demonstrou frustração crescente que a IA não está captando adequadamente…"
            aria-label="Motivo do takeover"
          />
          <span className="modal-form__hint">
            {trimmedLen}/{REASON_FLOOR} mínimo
          </span>
        </label>
        <div className="modal-form__callout modal-form__callout--warn">
          ⚠ O audit registra o force-takeover com o motivo obrigatório.
        </div>
        {failed && (
          <div className="banner banner--danger" role="alert">
            <div className="banner__body">
              <span className="banner__title">Não foi possível executar o takeover.</span>
            </div>
          </div>
        )}
        {outcome !== null && (
          <div className="modal-form__callout" role="status">
            {outcome}
          </div>
        )}
      </div>
    </ModalBase>
  );
}
